/**
 * Applies per-page SEO tags on every route change.
 *
 * The site is a single-page app: the browser only ever loads one index.html, so
 * without this every page would share that file's title and description. Google
 * treats duplicate descriptions across pages as a signal to ignore them, which
 * is why each route needs its own set written into the document head.
 *
 * The page table itself lives in `seo.config.js` at the project root, because
 * the sitemap generator reads the same data.
 */

import seoConfig from "../../seo.config.js";

const { SITE_URL, SITE_NAME, DEFAULT_IMAGE, PAGES } = seoConfig;

export { SITE_URL, SITE_NAME };

/** Page metadata keyed by route path, for O(1) lookup on navigation. */
const PAGES_BY_PATH = PAGES.reduce((acc, page) => {
  acc[page.path] = page;
  return acc;
}, {});

/**
 * Finds or creates a <meta> tag and sets its content.
 * @param {"name"|"property"} keyAttr Which attribute identifies the tag.
 */
function setMeta(keyAttr, keyValue, content) {
  let tag = document.head.querySelector(`meta[${keyAttr}="${keyValue}"]`);

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(keyAttr, keyValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
}

/**
 * Writes title, description, canonical and social tags for the given route.
 *
 * Unknown paths (the 404 catch-all) are marked noindex so Google does not put
 * "page not found" screens into results.
 *
 * @param {import("vue-router").RouteLocationNormalized} route
 */
export function applySeo(route) {
  const known = PAGES_BY_PATH[route.path];
  const url = `${SITE_URL}${route.path}`;
  const image = `${SITE_URL}${DEFAULT_IMAGE}`;

  // Tags persist across navigations, so the 404 route needs its own values —
  // otherwise it keeps whatever the previous page wrote.
  const page = known || {
    title: `Page Not Found | ${SITE_NAME}`,
    description:
      "This page could not be found. Browse our services, divisions and project photographs, or get in touch about a project.",
  };

  document.title = page.title;
  setMeta("name", "robots", known ? "index, follow" : "noindex, follow");
  setMeta("name", "description", page.description);
  setCanonical(url);

  setMeta("property", "og:title", page.title);
  setMeta("property", "og:description", page.description);
  setMeta("property", "og:url", url);
  setMeta("property", "og:image", image);
  setMeta("property", "og:type", "website");
  setMeta("property", "og:site_name", SITE_NAME);

  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", page.title);
  setMeta("name", "twitter:description", page.description);
  setMeta("name", "twitter:image", image);
}
